/**
 * Auth-refresh middleware — the load-bearing port of AIM's `axiosInstance`
 * interceptor stack into ky hooks. Behavior must stay equivalent to the
 * original; tests in `__tests__/auth-refresh.test.ts` enforce that.
 *
 * Three concerns layered into one module:
 *
 * 1. **Concurrent-401 queue.** Five parallel calls with an expired
 *    token result in *one* refresh, not five. While a refresh is in flight,
 *    subsequent 401s park on a promise that resolves with the new token; the
 *    parked requests then retry against the new token. A first 401 never
 *    bounces a consumer to `/login` while a refresh can still succeed.
 * 2. **`x-refreshed-token`.** The backend can rotate JWTs on a 200
 *    response by stamping `x-refreshed-token: <new>`. The client persists +
 *    notifies once per unique token (concurrent responses may all carry the
 *    same value).
 * 3. **ADR 100 — stale permissions.** Backend stamps
 *    `x-permissions-stale: true` + `x-permissions-version: <vN>` to indicate
 *    the user's permissions have moved. Client triggers a refresh and retries
 *    the original request, deduping on version so back-to-back stale flags
 *    don't fan out into N refreshes. The signal is also announced to
 *    subscribers, because rotating the token does not update the user object
 *    app state renders from.
 *
 * State is module-scoped (matches AIM's pattern). It is process-wide because
 * the refresh queue must coordinate across every request, regardless of which
 * call site started it. Tests reset state via `_resetAuthRefreshStateForTests`.
 */
import type { AfterResponseHook, BeforeRequestHook, KyInstance } from 'ky';
import {
  notifyPermissionsStale,
  notifyTokenRefreshChange,
  notifyTokenRefreshComplete,
} from '../listeners';
import type { StorageAdapter } from '../storage';

export type AuthScheme = 'JWT' | 'Bearer';

export interface RefreshResult {
  /** New access token. Returning null/undefined treats the refresh as failed. */
  token: string | null | undefined;
}

export interface AuthRefreshConfig {
  storage: StorageAdapter;
  /**
   * Callback that exchanges a refresh token for a new access token. Apps own
   * the URL and HTTP details if they need them — but most consumers should
   * configure `refreshEndpoint` instead and let the lib own the request.
   *
   * If both `refresh` and `refreshEndpoint` are set, the callback wins. If
   * neither is set, refresh capability is disabled (401s fall straight through
   * to `onAuthFailure`).
   *
   * Invariant: if you provide your own callback, it MUST NOT route
   * through the same `ky` instance — that would re-enter this middleware on
   * a 401-from-refresh and loop. The built-in `refreshEndpoint` path uses
   * raw `fetch()` and is therefore immune to this footgun.
   */
  refresh?: (refreshToken: string) => Promise<RefreshResult>;
  /**
   * Refresh URL the lib calls (via raw `fetch`, *not* through `ky`) to
   * exchange a refresh token for a new access token. Preferred over the
   * `refresh` callback — apps configure
   * `refreshEndpoint: '/api/v2/api-token-refresh/'` and the lib owns the
   * rest. The transport layer resolves relative paths against the configured
   * `baseURL`; absolute URLs are honored as-is.
   *
   * This is the load-bearing alternative to apps wiring up raw `axios` to
   * bypass the lib's middleware on the refresh request.
   */
  refreshEndpoint?: string;
  /**
   * Called after a refresh fails (refresh token absent, network error, no
   * token returned). Apps use it to redirect (`/`, `/login`, etc.) and wipe
   * other auth state. The middleware always calls `storage.clearAuthSession()`
   * before invoking this — apps only do app-specific redirects/cleanup.
   */
  onAuthFailure?: () => void;
  /**
   * Authorization scheme. AIM and SMS both use `JWT` today; the field is
   * here so a future migration to `Bearer` can be a config flip rather
   * than a code change. Default: 'JWT'.
   */
  authScheme?: AuthScheme;
}

interface AuthRefreshState {
  isRefreshing: boolean;
  refreshPromise: Promise<string | null> | null;
  /**
   * Last permissions-version we kicked a refresh on. Concurrent responses that
   * carry the same version short-circuit instead of fanning out into N
   * refreshes. Lifetime: process-wide; matches AIM behavior.
   */
  lastVersion: string | null;
  /**
   * Last token surfaced via `x-refreshed-token`. Concurrent responses can all
   * carry the same rotated token; we save + notify once per unique value.
   */
  lastNotifiedToken: string | null;
  /**
   * Last permissions version announced to subscribers. Separate from
   * `lastVersion`, which gates the token refresh: the two branches below
   * consume the signal differently, and one must not swallow the other's
   * dedupe.
   */
  lastAnnouncedVersion: number | null;
}

const state: AuthRefreshState = {
  isRefreshing: false,
  refreshPromise: null,
  lastVersion: null,
  lastNotifiedToken: null,
  lastAnnouncedVersion: null,
};

/**
 * Test hook — clears all module-scoped refresh state. Production callers
 * should never need to touch this.
 */
export function _resetAuthRefreshStateForTests(): void {
  state.isRefreshing = false;
  state.refreshPromise = null;
  state.lastVersion = null;
  state.lastNotifiedToken = null;
  state.lastAnnouncedVersion = null;
}

/**
 * `beforeRequest` hook — stamps the Authorization header from storage on
 * every outbound request. Skips when no token is set (login flow, public
 * endpoints).
 */
export function authBeforeRequest(
  config: AuthRefreshConfig,
): BeforeRequestHook {
  const scheme = config.authScheme ?? 'JWT';
  return ({ request }) => {
    const token = config.storage.getAccessToken();
    if (token) {
      request.headers.set('Authorization', `${scheme} ${token}`);
    }
  };
}

/**
 * Announce an ADR-100 staleness signal to subscribers, at most once per version.
 *
 * Rotating the access token is not the whole job: the permission set app state
 * renders from lives in the user object, which no token rotation touches. This
 * is the hop that lets app state re-read it.
 *
 * Subscribers may re-read the user with the pre-rotation token — that is
 * correct, because the current-user read reports database state, not whatever
 * the caller's token claims. A response to that read carrying the same version
 * dedupes here rather than looping.
 */
function announcePermissionsStale(response: Response): void {
  if (response.headers.get('x-permissions-stale') !== 'true') return;

  const raw = response.headers.get('x-permissions-version');
  const parsed = raw === null ? Number.NaN : Number(raw);
  const version = Number.isFinite(parsed) ? parsed : null;

  // A signal without a usable version cannot be deduped — announce it and let
  // the subscriber's own guard decide. The backend always sends one.
  if (version !== null && version === state.lastAnnouncedVersion) return;

  state.lastAnnouncedVersion = version;
  notifyPermissionsStale(version);
}

/**
 * `afterResponse` hook — handles all three flows above. Kept as one hook
 * because the order matters: `x-refreshed-token` must short-circuit the
 * stale-perms branch (the backend already rotated; don't double-rotate).
 *
 * @param getKy lazy accessor for the ky instance — the hook needs to retry
 *   requests through the same hook chain (so the retry stamps the new token
 *   on its way out). The transport builder hands this in to break the
 *   construction-time cycle (`buildTransport` returns the ky instance).
 */
export function authAfterResponse(
  config: AuthRefreshConfig,
  getKy: () => KyInstance,
): AfterResponseHook {
  const scheme = config.authScheme ?? 'JWT';

  return async ({ request, response }) => {
    // Announced before either branch, because both carry the signal and
    // branch 1 returns before branch 2 can see it. The backend stamps
    // `x-refreshed-token` only alongside the stale headers, and only for
    // hosts it auto-refreshes for — so which branch a response takes is an
    // environment property. Announcing from one of them would be silently
    // dead in the other.
    announcePermissionsStale(response);

    // Branch 1 — backend-rotated token. Always wins over the
    // stale-perms branch because the backend already did the work.
    const refreshedToken = response.headers.get('x-refreshed-token');
    if (refreshedToken) {
      if (refreshedToken !== state.lastNotifiedToken) {
        state.lastNotifiedToken = refreshedToken;
        config.storage.setAccessToken(refreshedToken);
        notifyTokenRefreshComplete(refreshedToken);
      }
      return response;
    }

    // Branch 2 — stale permissions (ADR 100). Refresh + retry.
    const stale = response.headers.get('x-permissions-stale');
    const version = response.headers.get('x-permissions-version');
    const refreshToken = config.storage.getRefreshToken();

    if (stale === 'true' && refreshToken) {
      if (version && version === state.lastVersion) {
        // Already refreshed for this version — don't fan out.
        return response;
      }
      state.lastVersion = version;
      const newToken = await acquireRefreshedToken(config);
      if (newToken) {
        return retryWithToken(getKy(), request, newToken, scheme);
      }
      return response;
    }

    // Branch 3 — 401 with concurrent-request queue. Either join
    // an in-flight refresh or kick a new one off.
    if (response.status === 401) {
      if (!refreshToken) {
        config.storage.clearAuthSession();
        config.onAuthFailure?.();
        return response;
      }

      const newToken = await acquireRefreshedToken(config);
      if (newToken) {
        return retryWithToken(getKy(), request, newToken, scheme);
      }

      config.storage.clearAuthSession();
      config.onAuthFailure?.();
      return response;
    }

    return response;
  };
}

/**
 * Built-in refresh implementation used when `refreshEndpoint` is configured.
 * Issues the refresh via raw `fetch` — deliberately NOT through the lib's
 * `ky` instance — so the auth-refresh middleware does not re-enter on a
 * 401-from-refresh response. The lib owns this recursion bypass so no app
 * has to.
 *
 * The transport layer resolves the endpoint to an absolute URL before
 * handing it to the middleware, so this function just `fetch`-es what it's
 * given.
 */
async function defaultRefreshViaFetch(
  refreshEndpoint: string,
  refreshToken: string,
): Promise<RefreshResult> {
  try {
    const res = await fetch(refreshEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: refreshToken }),
    });
    if (!res.ok) return { token: null };
    const body = (await res.json().catch(() => null)) as
      | { token?: string | null }
      | null;
    return { token: body?.token ?? null };
  } catch {
    return { token: null };
  }
}

/**
 * Dispatch between the configured refresh callback and the built-in
 * `refreshEndpoint` fetch path. The callback wins when both are set so that
 * tests + niche call-sites with custom refresh semantics keep working.
 */
async function callRefresh(
  config: AuthRefreshConfig,
  refreshToken: string,
): Promise<RefreshResult> {
  if (config.refresh) {
    return config.refresh(refreshToken);
  }
  if (config.refreshEndpoint) {
    return defaultRefreshViaFetch(config.refreshEndpoint, refreshToken);
  }
  // Misconfiguration — no refresh path. Treat as failed refresh so the
  // middleware drops to onAuthFailure rather than hanging on a never-resolved
  // promise.
  return { token: null };
}

/**
 * Returns the in-flight refresh promise if one is running, otherwise kicks
 * off a new refresh. Resolves with the new token (string) on success, null
 * on failure. The single-promise pattern is what makes 5 parallel 401s
 * collapse to 1 refresh + 5 retries.
 *
 * Exported (in addition to the internal hook usage) so apps can issue an
 * explicit manual refresh via `client.refreshAccessToken()` and have it
 * collapse with any in-flight auto-refresh — the userHandlers
 * permissions-changed handler and the Beacon `TokenRestorer` are its
 * consumers.
 */
export async function acquireRefreshedToken(
  config: AuthRefreshConfig,
): Promise<string | null> {
  if (state.isRefreshing && state.refreshPromise) {
    return state.refreshPromise;
  }

  const refreshToken = config.storage.getRefreshToken();
  if (!refreshToken) return null;

  state.isRefreshing = true;
  notifyTokenRefreshChange(true);

  state.refreshPromise = (async () => {
    try {
      const result = await callRefresh(config, refreshToken);
      const newToken = result?.token ?? null;
      if (newToken) {
        state.lastNotifiedToken = newToken;
        config.storage.setAccessToken(newToken);
        notifyTokenRefreshComplete(newToken);
        return newToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      state.isRefreshing = false;
      state.refreshPromise = null;
      notifyTokenRefreshChange(false);
    }
  })();

  return state.refreshPromise;
}

/**
 * Retry the original request with a freshly-obtained token. Routes through
 * the same ky instance so all hooks fire — most importantly, GPC and
 * vendor-org middleware re-stamp on the retry.
 *
 * The retry replaces the Authorization header before dispatch (the
 * `beforeRequest` hook would do this anyway, but doing it here is explicit
 * and matches AIM's behavior).
 */
async function retryWithToken(
  ky: KyInstance,
  originalRequest: Request,
  newToken: string,
  scheme: AuthScheme,
): Promise<Response> {
  const retryHeaders = new Headers(originalRequest.headers);
  retryHeaders.set('Authorization', `${scheme} ${newToken}`);

  // Body cannot be replayed off an already-consumed Request; clone before
  // any read. ky internally handles non-GET bodies, but to be safe we
  // build a fresh Request from the original's URL + method + cloned body.
  const cloned = originalRequest.clone();
  return ky(cloned.url, {
    method: cloned.method,
    headers: retryHeaders,
    body:
      cloned.method === 'GET' || cloned.method === 'HEAD'
        ? undefined
        : await cloned.blob(),
    // Suppress the auth-refresh hook on the retry to avoid an infinite loop
    // if the new token is *also* rejected. ky's retry config handles
    // transient failures; an auth retry-of-a-retry is a logout signal.
    hooks: { afterResponse: [] },
  });
}
